import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import useFillViewport from '../hooks/useFillViewport.js';
import { copyToClipboard } from '../lib/daily.js';
import { ASPECT, SHAPES, robed } from '../lib/silhouettes.js';
import { playCorrect, playWrong } from '../lib/sfx.js';
import {
  MAN_FT,
  dealRun,
  feet,
  fromSlider,
  inPeople,
  metres,
  offBy,
  rankFor,
  scoreGuess,
  shareRun,
  toSlider,
} from '../lib/toScale.js';

/**
 * To Scale.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * Scripture gives exact dimensions for a surprising number of things and
 * almost nobody pictures any of them correctly. The ark of the covenant is
 * under four feet long. Goliath is nearly ten feet tall. The point of the game
 * is the moment your guess slides to the real answer and you find out how far
 * off you were — so the whole screen is built around that one animation, and
 * everything else stays out of its way.
 *
 * ── How it reads ─────────────────────────────────────────────────────────────
 *
 * A 5ft 11in man stands on the left of every round and never changes size. He
 * is the only thing on screen that stays constant, which is what makes the
 * subject beside him mean anything. The dashed gold line is your guess; when
 * you lock it in, the silhouette *grows or shrinks into* the true size rather
 * than cutting to it, because the distance travelled is the answer.
 *
 * ── The two things that took real work ───────────────────────────────────────
 *
 * The camera fits the subject's whole bounding box, not the dimension being
 * asked about. Frame a 450ft-long ark as though it were 450ft tall and the
 * boat becomes a sliver at the bottom of an empty screen.
 *
 * The ground line tracks the card below it. The reveal panel is roughly twice
 * the height of the guess panel, so a fixed horizon puts the feet — and the
 * "5 FT 11" caption that makes the comparison legible — behind the card at
 * exactly the moment the player needs to see them.
 */

const REVEAL_MS = 1300;
/** A robed figure is about 0.44 as wide as it is tall. */
const MAN_HALF = MAN_FT * 0.22;
/** Below this drawn height a single figure stops reading, and a row is used. */
const LEGIBLE_PX = 26;

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

export default function ToScale({
  best = 0,
  runs = 0,
  signedIn = false,
  soundOn = true,
  onNeedAccount,
  onUpgrade,
  onFinish,
  onExit,
}) {
  const [deck, setDeck] = useState(() => dealRun());
  const [idx, setIdx] = useState(0);
  const [guess, setGuess] = useState(() => deck[0].start);
  const [phase, setPhase] = useState('guess'); // guess | reveal | over
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  // Drawn size lives in a ref, not in state: it changes every animation frame
  // and nothing in the DOM depends on it, so putting it in state would be
  // sixty React renders a second to paint a canvas React does not own.
  const shownRef = useRef(0);
  const animRef = useRef(null);
  const rafRef = useRef(0);

  const round = deck[Math.min(idx, deck.length - 1)];

  // Opening a round resets the slider to that round's starting guess.
  useEffect(() => {
    if (!round) return;
    setGuess(round.start);
    shownRef.current = round.start;
    animRef.current = null;
  }, [round]);

  const total = useMemo(() => history.reduce((sum, h) => sum + h.points, 0), [history]);
  const max = deck.length * 100;

  /*
   * Fit the round to the phone — see useFillViewport for why the room has to
   * be measured rather than expressed in vh.
   *
   * Only the section is sized here; the split between canvas and panel is left
   * to flex, with the panel on a fixed share of the section rather than on its
   * content. That keeps the canvas the same size across the guess and the
   * reveal — and the reveal *is* an animation on that canvas, so resizing it
   * mid-flight would throw the silhouette about.
   *
   * Measured before this: "Lock it in" was off the bottom of the screen on
   * every phone but the largest, and the reveal ran up to 482px past it.
   */
  useFillViewport(
    sectionRef,
    phase === 'over' ? 'over' : 'round',
    (room) => {
      const section = sectionRef.current;
      if (!section) return;
      if (phase === 'over') {
        section.style.height = '';
        return;
      }
      // Only the section is sized here. The split between canvas and panel is
      // left to flex, so it stays constant across the guess and the reveal.
      section.style.height = `${Math.round(Math.max(280, room))}px`;
    },
    [phase, idx],
  );

  /* ── the drawing ─────────────────────────────────────────────────────────── */

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !round) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    const W = Math.max(1, Math.round(rect.width));
    const H = Math.max(1, Math.round(rect.height));
    if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (animRef.current) {
      const a = animRef.current;
      const t = clamp((performance.now() - a.at) / REVEAL_MS, 0, 1);
      shownRef.current = a.from + (a.to - a.from) * easeInOut(t);
      if (t >= 1) animRef.current = null;
    }

    const size = shownRef.current || round.start;

    // Fit the whole silhouette, not just the dimension in the question.
    const ratio = ASPECT[round.shape] ?? 1;
    const subjectW = round.dim === 'height' ? size * ratio : size;
    const subjectH = round.dim === 'height' ? size : size * ratio;
    const vSpan = Math.max(subjectH, MAN_FT) * 1.18;
    // The 1.08 is overhang: a temple roof, the ark of the covenant's carrying
    // poles and Goliath's spear all reach past the dimension being asked for,
    // and without it they get clipped at the right edge.
    const hSpan = subjectW * 1.08 + MAN_HALF * 2 + 1.4;

    // Room at the top for the question, at the bottom for the caption.
    const topPad = H * 0.13;
    const bottomPad = 26;
    const availH = H - topPad - bottomPad;
    const pxPerFt = Math.min(availH / vSpan, (W - 28) / hSpan);

    /*
     * A wide subject is width-constrained — the ark is 450ft long and 45ft
     * tall, so fitting its length leaves the drawing only a fraction of the
     * height. Sitting that on a fixed horizon strands it at the bottom of a
     * mostly empty frame, so the ground line moves to centre whatever was
     * actually drawn. Order matters: the scale has to be settled first,
     * because the horizon depends on how tall the result turned out.
     */
    const contentH = Math.max(subjectH, MAN_FT) * pxPerFt;
    const gy = Math.min(H - bottomPad, topPad + (availH + contentH) / 2);

    // sky
    const sky = ctx.createLinearGradient(0, 0, 0, gy);
    sky.addColorStop(0, '#0a0d16');
    sky.addColorStop(0.55, '#1a1a20');
    sky.addColorStop(1, '#3a2a1e');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, W, gy);
    // Lighter than the silhouettes, or a dark subject standing on the ground
    // line dissolves into it exactly where the two need to be told apart.
    ctx.fillStyle = '#1c1622';
    ctx.fillRect(0, gy, W, H - gy);

    const glow = ctx.createRadialGradient(W * 0.72, gy, 0, W * 0.72, gy, W * 0.62);
    glow.addColorStop(0, 'rgba(226,150,80,.40)');
    glow.addColorStop(1, 'rgba(226,150,80,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, gy);

    // About five grid lines, whatever the scale.
    const rawStep = vSpan / 5;
    const mag = 10 ** Math.floor(Math.log10(rawStep));
    const step =
      [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => vSpan / s <= 6.5) ?? 10 * mag;
    ctx.strokeStyle = 'rgba(232,220,196,.10)';
    ctx.lineWidth = 1;
    ctx.fillStyle = 'rgba(232,220,196,.26)';
    ctx.font = '500 10px system-ui, sans-serif';
    for (let f = step; f <= vSpan; f += step) {
      const y = gy - f * pxPerFt;
      if (y < topPad) break;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(W, y);
      ctx.stroke();
      ctx.fillText(`${f % 1 ? f.toFixed(1) : f} ft`, 8, y - 5);
    }

    ctx.fillStyle = 'rgba(232,220,196,.20)';
    ctx.fillRect(0, gy, W, 1);

    // the subject, drawn first so the measuring figure is never behind it
    const drawShape = SHAPES[round.shape];
    const manX = 18 + MAN_HALF * pxPerFt;
    if (drawShape) {
      const px = size * pxPerFt;
      const near = manX + (MAN_HALF + subjectW * 0.5 + 0.5) * pxPerFt;
      const wanted = manX + (MAN_HALF + 0.9 + subjectW * 0.5) * pxPerFt;
      const sx = clamp(wanted, near, W - subjectW * 0.54 * pxPerFt - 12);
      ctx.fillStyle = phase === 'guess' ? '#0d0a12' : '#050409';
      drawShape(ctx, sx, gy, px);
    }

    /*
     * The constant: a man, always his true height.
     *
     * He is drawn in the accent rather than in silhouette because next to the
     * ark he is two pixels per foot — about eleven pixels tall — and a black
     * figure that size against dark ground is simply not there. He is the
     * instrument you are measuring with, so he takes the instrument's colour,
     * and stays legible at every scale in the pool.
     */
    const manPx = MAN_FT * pxPerFt;
    ctx.fillStyle = 'rgba(201,168,76,.92)';
    robed(ctx, manX, gy, manPx);

    /*
     * When one figure is too small to read — four pixels tall, beside the ark
     * — a whole row of them is drawn instead, at exactly the same true scale,
     * spanning the subject's footprint. Nobody can judge a four-pixel mark,
     * but everybody can read "that is about seventy people long", and the row
     * says it without enlarging anybody.
     */
    if (manPx < LEGIBLE_PX && drawShape) {
      const gap = MAN_FT * 0.62 * pxPerFt;
      const span = subjectW * pxPerFt;
      const count = Math.min(90, Math.floor(span / gap));
      ctx.save();
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = 'rgba(201,168,76,.9)';
      for (let i = 0; i < count; i += 1) {
        robed(ctx, manX + gap * (i + 1), gy, manPx);
      }
      ctx.restore();
    }

    ctx.fillStyle = 'rgba(201,168,76,.75)';
    ctx.font = '600 9px system-ui, sans-serif';
    if (manPx < LEGIBLE_PX) {
      // Left-aligned: centred on a figure this small, the caption runs off the
      // edge of the canvas and loses its first characters.
      ctx.textAlign = 'left';
      ctx.fillText('EACH 5 FT 11', 8, gy + 15);
    } else {
      ctx.textAlign = 'center';
      ctx.fillText('5 FT 11', manX, gy + 15);
      ctx.textAlign = 'left';
    }

    if (phase === 'guess') {
      ctx.save();
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = 'rgba(201,168,76,.65)';
      ctx.lineWidth = 1.5;
      const top = gy - subjectH * pxPerFt;
      ctx.beginPath();
      ctx.moveTo(0, top);
      ctx.lineTo(W, top);
      ctx.stroke();
      ctx.restore();
    }

    const haze = ctx.createLinearGradient(0, gy - H * 0.16, 0, gy);
    haze.addColorStop(0, 'rgba(58,42,30,0)');
    haze.addColorStop(1, 'rgba(58,42,30,.5)');
    ctx.fillStyle = haze;
    ctx.fillRect(0, gy - H * 0.16, W, H * 0.16);

    rafRef.current = requestAnimationFrame(draw);
  }, [round, phase]);

  useEffect(() => {
    if (phase === 'over') return undefined;
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw, phase]);

  /* ── the game ────────────────────────────────────────────────────────────── */

  const lock = () => {
    if (phase !== 'guess' || !round) return;
    const points = scoreGuess(guess, round.truth);
    animRef.current = { from: guess, to: round.truth, at: performance.now() };
    setHistory((h) => [...h, { id: round.id, guess, truth: round.truth, points }]);
    setPhase('reveal');
    if (soundOn) (points >= 60 ? playCorrect : playWrong)();
  };

  const next = () => {
    if (idx + 1 < deck.length) {
      setIdx(idx + 1);
      setPhase('guess');
      return;
    }
    setPhase('over');
    onFinish?.(total, history);
  };

  const again = () => {
    setDeck(dealRun());
    setIdx(0);
    setHistory([]);
    setPhase('guess');
  };

  // One complete run, then an account. `runs` already counts the run that has
  // just ended, so this is true on the first results screen an anonymous
  // player sees — after they have played, never before.
  const gateReplay = !signedIn && runs >= 1;
  const shareable = useMemo(() => shareRun(total, max, history), [total, max, history]);

  /* ── results ─────────────────────────────────────────────────────────────── */

  if (phase === 'over') {
    const beaten = total > best;
    return (
      <section className="screen ts">
        <div className="ts-over">
          <p className="ts-over__eyebrow">{beaten ? 'New best' : 'Run complete'}</p>
          <p className="ts-over__score">{total}</p>
          <p className="ts-over__label">out of {max}</p>
          <p className="ts-over__rank">{rankFor(total)}</p>

          <ol className="ts-over__list">
            {history.map((h) => {
              const item = deck.find((d) => d.id === h.id);
              return (
                <li key={h.id} className="ts-over__row">
                  <span className="ts-over__row-q">{item?.q.replace(/^How\s+\w+\s+(was|were)\s+/i, '')}</span>
                  <span className="ts-over__row-you">{feet(h.guess)}</span>
                  <span className="ts-over__row-truth">{feet(h.truth)}</span>
                  <span className="ts-over__row-pts">{h.points}</span>
                </li>
              );
            })}
          </ol>

          <p className="ts-over__caveat">
            A cubit is about 18 inches — the length of a forearm. Scripture doesn’t
            define it, so every figure here uses that standard estimate.
          </p>

          {gateReplay ? (
            <div className="ts-gate">
              <p className="ts-gate__lead">
                {beaten ? 'That is your best run.' : `${total} out of ${max}.`} It lives
                in this browser only — clear it and it is gone.
              </p>
              <button
                type="button"
                className="btn btn--primary btn--block btn--lg"
                onClick={onNeedAccount}
              >
                Make a free account to keep playing
              </button>
              <p className="ts-gate__note">
                Free, takes a moment. Keeps your best score and carries it to your phone.
              </p>
              <button type="button" className="ts-gate__more" onClick={onUpgrade}>
                Or see what Premium opens up
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn--primary btn--block btn--lg" onClick={again}>
              Play again — a different five
            </button>
          )}

          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={async () => {
              if (navigator.share) {
                try {
                  await navigator.share({ text: shareable });
                  return;
                } catch {
                  /* dismissed — fall through to copy */
                }
              }
              if (await copyToClipboard(shareable)) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }
            }}
          >
            <Icon name="share" size={15} />
            {copied ? 'Copied' : 'Share your run'}
          </button>

          <button type="button" className="btn btn--ghost btn--block" onClick={onExit}>
            Back to practice
          </button>
        </div>
      </section>
    );
  }

  const last = history.at(-1);

  return (
    <section ref={sectionRef} className="screen ts">
      <header className="ts__head">
        <button type="button" className="icon-btn" onClick={onExit} aria-label="Leave the game">
          <Icon name="close" size={18} />
        </button>
        <div className="ts__progress">
          {deck.map((d, i) => (
            <span key={d.id} className={`ts__pip${i < idx ? ' is-done' : i === idx ? ' is-now' : ''}`} />
          ))}
        </div>
        <span className="ts__total">{total} pts</span>
      </header>

      <div ref={stageRef} className="ts__stage">
        <canvas ref={canvasRef} className="ts__canvas" />
        <h2 className="ts__q">{round?.q}</h2>
      </div>

      {phase === 'guess' ? (
        <div className="ts-panel">
          <div className="ts-panel__body">
            <p className="ts-panel__readout">
              {feet(guess)}
              <small>{metres(guess)}</small>
            </p>
            <input
            type="range"
            className="ts-slider"
            min="0"
            max="1000"
            value={Math.round(toSlider(guess, round.min, round.max) * 1000)}
            aria-label={round?.q}
              onChange={(e) =>
                setGuess(fromSlider(Number(e.target.value) / 1000, round.min, round.max))
              }
            />
          </div>
          <button type="button" className="btn btn--primary btn--block btn--lg" onClick={lock}>
            Lock it in
          </button>
        </div>
      ) : (
        <div className="ts-panel ts-panel--reveal">
          <div className="ts-panel__body">
            <p className="ts-panel__verdict">
            <b>{feet(round.truth)}</b>
            <span>
              {metres(round.truth)} · {offBy(last?.guess, round.truth)} · +{last?.points}
            </span>
          </p>
            <p className="ts-panel__people">{inPeople(round.truth, round.dim)}</p>
            <p className="ts-panel__maths">{round.maths}</p>
            <p className="ts-panel__verse">{round.verse}</p>
            <p className="ts-panel__ref">{round.ref}</p>
          </div>
          <button type="button" className="btn btn--primary btn--block btn--lg" onClick={next}>
            {idx + 1 < deck.length ? 'Next' : 'See your total'}
          </button>
        </div>
      )}
    </section>
  );
}

